import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
} from '@material-ui/core';
import CollapseSection from 'app/shared/components/list/CollapseSection';
import ClassName from 'app/styles/ClassName';
import styledFuncs from 'app/styles/styledFuncs';
import { Fragment } from 'react';
import styled from 'styled-components/macro';
import { Except } from 'type-fest';

const ChildListItem: typeof ListItem = styled(ListItem).attrs({
  className: ClassName.NO_AUTO_MARGIN,
})`
  padding-left: ${styledFuncs.pad(4)};
`;

const StyledList = styled(List).attrs({
  dense: true,
})`
  padding: 0;
  margin: ${styledFuncs.pad(0.5)} 0;
  padding-right: ${styledFuncs.pad()};
  flex: 1 1 80px;
  align-self: center;
  overflow: auto;
  min-height: 200px;
  .MuiButtonBase-root {
    /* makes the checkbox and dropdown arrow smaller */
    padding: 4px;
    &:first-child {
      margin-right: ${styledFuncs.pad(0.5)};
    }
  }
  .MuiListItemText-root > span {
    font-size: ${styledFuncs.pxToRem(13)};
  }
  .MuiListSubheader-root {
    line-height: 25px;
    background: white;
  }
`;

type SelectableCategory<T> = T & { selected: boolean };
export type DetailedGroup<T> = T & {
  variants: SelectableCategory<T>[];
  childlessAndSelected?: boolean; // only to be used if variants = []
  parent?: number;
};
export type DetailedGroupCollected<T> = {
  area: string;
  groups: DetailedGroup<T>[];
};

export const groupChecked = <T,>(group: DetailedGroup<T>): boolean =>
  // NOTE: if childlessAndSelected is false, then variants = [],
  // so checking .some on it returns false too.
  group.childlessAndSelected || group.variants.some(v => v.selected);
const groupIndeterminate = <T,>(group: DetailedGroup<T>): boolean =>
  groupChecked(group) &&
  !group.childlessAndSelected && // childless groups should never be indeterminate == true. Always == false.
  !group.variants.every(v => v.selected);

type GroupListItemProps<T extends BaseItem> = {
  group: DetailedGroup<T>;
  childlessGroupIds: number[];
  handleGroupClick?: (item: DetailedGroup<T>) => () => void;
  handleVariantClick?: (item: T) => () => void;
};
export const GroupListItem = <T extends BaseItem>({
  group,
  childlessGroupIds,
  handleGroupClick,
  handleVariantClick,
}: GroupListItemProps<T>): JSX.Element => (
  <CollapseSection
    noTopMargin
    noButton={childlessGroupIds.includes(group.id)}
    key={group.id}
    title={
      <ListItem>
        <Checkbox
          checked={groupChecked(group)}
          indeterminate={groupIndeterminate(group)}
          onClick={handleGroupClick?.(group)}
        />
        <ListItemText>{group.displayName}</ListItemText>
      </ListItem>
    }
  >
    {group.variants.map(variant => (
      <ChildListItem key={variant.id}>
        <Checkbox
          checked={variant.selected}
          onClick={handleVariantClick?.(variant)}
        />
        <ListItemText>{variant.displayName}</ListItemText>
      </ChildListItem>
    ))}
  </CollapseSection>
);

type BaseItem = { id: number; displayName: string };
type Props<T extends BaseItem> = Except<
  GroupListItemProps<T>,
  'group'
> & {
  availableGroupsDetailedCollected: DetailedGroupCollected<T>[];
};

export default function TreeSelectList<T extends BaseItem>({
  availableGroupsDetailedCollected,
  ...listItemProps
}: Props<T>): JSX.Element {
  return (
    <StyledList>
      {availableGroupsDetailedCollected.map(({ area, groups }) => (
        <Fragment key={area}>
          <ListSubheader>{area}</ListSubheader>
          {groups.map(group => (
            <GroupListItem
              key={group.id}
              {...{ group }}
              {...listItemProps}
            />
          ))}
        </Fragment>
      ))}
    </StyledList>
  );
}
